import * as React from 'react';
import { queryNoTracking } from '../../services/DataService';
import { accentUtils, from, showDialog } from '../../services/HelperService';
import { getProduct } from '../../services/ProductService';
import { AccentQueryShapingAssistantDlg } from '../workflow/AccentQueryShapingAssistantDlg';

const query_EndQueryShapingSession = queryNoTracking("Workflow_QueryShaping_EndSession", 0, true);

/**
 * Whether the explainer can say anything useful about a product: it needs a locally
 * defined product (the oracle investigates the definition blobs). Marketplace products
 * have no local definition — hosts hide the wand for them rather than open a chat
 * that can only answer "I couldn't load anything".
 */
export const canExplainProduct = (productID) => {
    if (accentUtils.isEmpty(productID)) return false;
    const product = getProduct(productID, null);
    return !accentUtils.isNull(product) && product.IsMarketplaceProduct !== true;
};

/**
 * Builds the explainContextJson snapshot for a set of job lines (entities or
 * Job_PriceDetails rows — both carry the same field names). Each line is trimmed to
 * what the explainer needs: the ids the server loads definitions by
 * (ProductID/ProductVersionID), the line's IDENTITY BY NAME (product name/code/
 * version, supplier, multi-config membership — so "what product is this?" is answered
 * with names, never a guid recital), the dimensions, the chosen options and the
 * line's PriceModel (the pricing engine's breakdown, which also identifies the
 * price list).
 */
export const buildJobLineExplainContext = (lines) => {

    const parseJson = s => {
        try { return accentUtils.isEmpty(s) ? null : JSON.parse(s); }
        catch { return null; }
    };

    // A line saved before versioning (or a line the pricing engine hasn't stamped
    // yet) carries an empty ProductVersionID. Guid.Empty keys the DRAFT definition
    // server-side, which is the review profile's document — so resolve the ACTIVE
    // published version from the product cache here and send that instead.
    const resolveVersionID = (l) => {
        if (!accentUtils.isEmpty(l.ProductVersionID) && l.ProductVersionID !== accentUtils.getEmptyGuid()) {
            return l.ProductVersionID;
        }
        const active = getProduct(l.ProductID, null); // null version resolves to the ACTIVE row
        return (!accentUtils.isNull(active) && active.IsDraft !== true && !accentUtils.isEmpty(active.VersionID))
            ? active.VersionID
            : l.ProductVersionID;
    };

    const contextLines = (lines ?? []).map(l => ({
        ID: l.ID,
        ProductID: l.ProductID,
        ProductVersionID: resolveVersionID(l),
        ProductName: l.ProductName,
        ProductCode: l.ProductCode,
        ProductVersion: l.ProductVersion,
        MultiProductID: l.MultiProductID,
        MultiProductVersionID: l.MultiProductVersionID,
        MultiGroupID: l.MultiGroupID,
        SupplierID: l.SupplierID,
        SupplierName: l.SupplierName,
        Width: l.Width,
        Drop: l.Drop,
        options: parseJson(l.OrderLineOptionsData),
        priceModel: parseJson(l.PriceModel),
    }));

    const priceListID = from(contextLines)
        .where(l => !accentUtils.isEmpty(l.priceModel?.PriceListID))
        .select(l => l.priceModel.PriceListID)
        .firstOrDefault() ?? null;

    return JSON.stringify({
        lines: contextLines,
        priceList: accentUtils.isEmpty(priceListID) ? null : { priceListID },
    });
};

/**
 * Shared launcher for PRODUCT EXPLAINER chat sessions (session profile
 * "productExplainer"): diagnostic questions about a configured job line — "why can't I
 * see option X when I select Y?", "why is option Z priced at $20?".
 *
 * The launcher HOLDS one chat session per key (a job line id), so the user can close
 * the chat, keep working, then reopen the wand on the same line and continue the same
 * conversation. Hosts own one launcher instance and MUST call dispose() on teardown —
 * it drops the definitions the product spec oracle cached server-side for each session
 * (best-effort; the cache TTL covers a lost call).
 *
 * Two ways to surface the chat:
 * - open(): the modal assistant dialog (used by the price details rows);
 * - getSession()/setSession(): the raw session store, for hosts that render the docked
 *   ProductExplainerChatPanel themselves (the job line editor's split layout).
 *
 * buildContextJson is a FUNCTION returning the explainContextJson snapshot; the chat
 * calls it on every send, so a resumed chat always reports the host's current state.
 */
export class ProductExplainerLauncher {

    constructor() {
        this.sessions = {};
    }

    getSession(key) {
        return this.sessions[key];
    }

    setSession(key, session) {
        if (!accentUtils.isNull(session)) {
            this.sessions[key] = session;
        }
    }

    open(key, buildContextJson) {
        return showDialog(<AccentQueryShapingAssistantDlg
            sessionProfile="productExplainer"
            // Kept on the server per line (the key is the job line's id): the explainer's
            // chat survives the page and the browser, not just this launcher instance.
            sessionHost={{ hostEntity: 'JobLine', hostEntityID: Number(key) > 0 ? Number(key) : null, hostSubKey: Number(key) > 0 ? null : `${key ?? ''}` }}
            existingQueries={[]}
            explainContextJson={buildContextJson}
            initialSession={this.sessions[key]}
        />).then(res => {
            if (res?.session) {
                this.sessions[key] = res.session;
            }
        });
    }

    dispose() {
        Object.values(this.sessions).forEach(session => {
            const diagnosticSessionID = session?.diagnosticSessionID;
            if (diagnosticSessionID) {
                query_EndQueryShapingSession.getFirstOrDefault({ diagnosticSessionID }).catch(() => { });
            }
        });
        this.sessions = {};
    }
}
